import { useEffect, useMemo, useState } from "react";
import { Separator, Skeleton } from "@racoon/ui";
import { Namespace, useT } from "@racoon/i18n";
import { skipToken } from "@reduxjs/toolkit/query";
import { useSearchParams } from "react-router-dom";
import { isNumericId } from "@racoon/lib";
import { useSelectedCompany } from "~/shared/store/company/hooks/useSelectedCompany";
import { useListTrucksQuery } from "~/shared/api/truck/truck.grpc";
import { useListPickupLocationsQuery } from "~/shared/api/pickup-locations/pickup-locations.grpc";
import {
  useGetPickupLocationGroupByIdQuery,
  useListPickupLocationGroupsQuery,
} from "~/shared/api/pickup-location-groups/pickup-location-groups.grpc";
import { MapWithGeofence, MapObject } from "~/features/map-view/ui/map-view";
import {
  CollapsibleMapPanel,
  MAP_PANEL_CONTROL_OFFSET,
} from "~/features/map-view/components/CollapsibleMapPanel";
import { useMapPanelCollapsed } from "~/features/map-view/hooks/use-map-panel-collapsed";
import { orderGroupContainerGroups } from "../model/helpers";
import { useServiceDayFilter } from "../model/use-service-day-filter";
import { GroupListPanel } from "../components/group-list.panel";
import { GroupDetailPanel } from "../components/group-detail.panel";
import { CreateGroupModal } from "../components/create-group.modal";

export const PickupLocationGroupsPage = () => {
  const t = useT();
  const { selectedCompanyId } = useSelectedCompany();
  const [searchParams] = useSearchParams();
  // A link from the pickup-locations registry (`?group=<id>`) opens on that group.
  const [selectedGroupId, setSelectedGroupId] = useState<bigint | null>(() => {
    const linked = searchParams.get("group");
    return linked && isNumericId(linked) ? BigInt(linked) : null;
  });
  const [createOpen, setCreateOpen] = useState(false);
  const { collapsed: mapCollapsed, toggle: toggleMapCollapsed } =
    useMapPanelCollapsed("pickup-location-groups");
  // Optimistic order after a drag in the table, until the refetched group
  // confirms it. Owned here so the table and the map number rows the same way.
  const [localOrder, setLocalOrder] = useState<string[] | null>(null);

  const skip = !selectedCompanyId;

  const { data: groupsData, isLoading: groupsLoading } = useListPickupLocationGroupsQuery(
    { ownerOrganizationIds: selectedCompanyId ? [BigInt(selectedCompanyId)] : [] },
    { skip },
  );
  const groups = useMemo(() => groupsData?.pickupLocationGroups ?? [], [groupsData]);

  const { data: locationsData } = useListPickupLocationsQuery(
    { serviceProviderIds: selectedCompanyId ? [BigInt(selectedCompanyId)] : [] },
    { skip },
  );
  const locations = useMemo(() => locationsData?.pickupLocations ?? [], [locationsData]);

  const { data: trucksData } = useListTrucksQuery(
    { ownerOrganizationIds: selectedCompanyId ? [BigInt(selectedCompanyId)] : [] },
    { skip },
  );
  const trucks = useMemo(() => trucksData?.trucks ?? [], [trucksData]);

  // Auto-select the first group once loaded (or after the selected one is deleted).
  // Not before: an empty list while loading would drop the group a link opened on.
  useEffect(() => {
    if (!groupsData) return;
    if (groups.length === 0) {
      setSelectedGroupId(null);
      return;
    }
    // By string: ids from the list arrive as text at runtime, the linked one is a bigint.
    const stillExists =
      selectedGroupId !== null && groups.some((g) => String(g.id) === String(selectedGroupId));
    if (!stillExists) {
      setSelectedGroupId(groups[0].id);
    }
  }, [groupsData, groups, selectedGroupId]);

  // skipToken instead of a fabricated request: a fake {id: 0} arg would create
  // a real cache entry for a non-existent group.
  const { data: groupDetail, isFetching: detailLoading } = useGetPickupLocationGroupByIdQuery(
    selectedGroupId !== null && selectedCompanyId
      ? { id: selectedGroupId, ownerOrganizationId: BigInt(selectedCompanyId) }
      : skipToken,
  );

  // The refetch after a reorder confirms the order; drop the optimistic one.
  // Also resets when another group is selected.
  useEffect(() => {
    setLocalOrder(null);
  }, [groupDetail?.containerGroupLinks]);

  // Owned here, like localOrder: the table and the map must narrow to the same rows.
  const serviceDay = useServiceDayFilter({
    group: groupDetail,
    groups,
    serviceProviderId: selectedCompanyId ? BigInt(selectedCompanyId) : undefined,
  });
  const dueByCgId = serviceDay.dueJobTypesByCgId;

  // Map pins: unique pickup locations of the group's bound container groups, in
  // the table's display order. A pin is labelled with the row number of the
  // location's first container group, so pin 1 is the table's first row — a
  // location bound by several container groups keeps only its first number.
  const mapObjects = useMemo((): MapObject[] => {
    if (!groupDetail) return [];
    const locationById = new Map(locations.map((loc) => [String(loc.id), loc]));
    const seen = new Set<string>();
    const objects: MapObject[] = [];
    orderGroupContainerGroups(groupDetail, localOrder, dueByCgId).forEach((cg, i) => {
      const id = String(cg.pickupLocationId);
      if (seen.has(id)) return;
      seen.add(id);
      const location = locationById.get(id);
      // Explicit finite check: 0 is a valid coordinate, unlike `!lat`.
      if (!location || !Number.isFinite(location.lat) || !Number.isFinite(location.lon)) return;
      objects.push({
        id: Number(location.id),
        name: location.name,
        type: "pickup",
        lat: location.lat,
        lon: location.lon,
        localIndex: i + 1,
        geometry: location.geometry,
      });
    });
    return objects;
  }, [groupDetail, locations, localOrder, dueByCgId]);

  return (
    <div className="flex w-full min-w-0 min-h-0 flex-1 gap-4 rounded-2xl bg-white p-4 shadow-sm">
      <GroupListPanel
        groups={groups}
        selectedGroupId={selectedGroupId}
        onSelect={setSelectedGroupId}
        onCreateClick={() => setCreateOpen(true)}
        loading={groupsLoading}
      />

      <Separator orientation="vertical" className="h-auto" />

      <div className="flex min-w-0 flex-1 gap-4">
        <div className="flex min-w-0 flex-1 flex-col">
          {groupsLoading && (
            <div className="flex flex-col gap-3">
              <Skeleton className="h-8 w-64" />
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-64 w-full" />
            </div>
          )}
          {!groupsLoading && groupDetail && (
            <GroupDetailPanel
              group={groupDetail}
              locations={locations}
              trucks={trucks}
              loading={detailLoading}
              serviceDay={serviceDay}
              localOrder={localOrder}
              onLocalOrderChange={setLocalOrder}
              onDeleted={() => setSelectedGroupId(null)}
            />
          )}
          {!groupsLoading && !groupDetail && (
            <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
              {groups.length === 0
                ? t(Namespace.PickupLocation, "groups.noGroups")
                : t(Namespace.PickupLocation, "groups.selectGroup")}
            </div>
          )}
        </div>

        {/* The panel (and its toggle) only exists from xl up — below that
            there is nothing to collapse. */}
        <CollapsibleMapPanel
          className="hidden xl:block"
          collapsed={mapCollapsed}
          onToggle={toggleMapCollapsed}
          expandedWidthClassName="w-[420px] 2xl:w-[541px]"
        >
          <MapWithGeofence
            className="h-full w-full z-0"
            objects={mapObjects}
            collapsed={mapCollapsed}
            controlStackClassName={MAP_PANEL_CONTROL_OFFSET}
          />
        </CollapsibleMapPanel>
      </div>

      {selectedCompanyId && (
        <CreateGroupModal
          open={createOpen}
          onOpenChange={setCreateOpen}
          ownerOrganizationId={selectedCompanyId}
          trucks={trucks}
          onCreated={(group) => setSelectedGroupId(group.id)}
        />
      )}
    </div>
  );
};
